import { TypographyP2 } from '@heidi/design-system/typography';
import { clearLibraryDocumentPreview } from '@heidi/evidence';
import type { NavigatePort } from '@heidi/runtime/shell';
import { useQuerySessionById } from '@heidi/session/data';
import { useSetAtom } from 'jotai';
import { SquarePen, X } from 'lucide-react';
import { useCallback, useLayoutEffect, useState, type ComponentType } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { ProjectDetail } from '../../../contracts/projects.types';
import {
  LEGACY_CHAT_HOME_PATH,
  WORK_CHAT_HOME_PATH,
} from '../../../contracts/projects/chat-home-path';
import { getChatHref } from '../../../contracts/projects/chatRouting';
import { useResetProjectsComposerContextCore } from '../../../data/projects/resetProjectsComposerContext';
import {
  activeWorkPanelProjectIdAtom,
  workPanelProjectIdAtomFamily,
} from '../../../data/projects/workPanel';
import { useVoiceHostedSidebarOpen } from '../../voice-assistant/workspace/useVoiceHostedSidebarOpen';
import { voiceSessionRhsHostCountAtom } from '../../voice-assistant/workspace/voiceSessionRhsHost';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import { CreatedChatArtifactsButton } from './CreatedChatArtifactsButton';
import { HeaderIconButton } from './HeaderIconButton';
import { ProjectChatMenu } from './ProjectChatMenu';
import type { ProjectHeaderActionsProps } from './ProjectHeaderActions';
import { ProjectHeaderActions } from './ProjectHeaderActions';
import type { ProjectTitleClusterProps } from './ProjectTitleCluster';
import { ProjectTitleCluster } from './ProjectTitleCluster';
import { useSessionWorkPanelEvidenceChrome } from './SessionWorkPanelEvidenceChromeContext';
import type { SessionWorkProjectSelectorProps } from './SessionWorkProjectSelector';
import { SessionWorkProjectSelector } from './SessionWorkProjectSelector';

export interface SessionWorkVoiceSidebarSyncProps {
  setOpen: (open: boolean) => void;
  /** Voice paints in the session RHS slot right now, as the app's placement resolves it. */
  voiceInWorkSlot: boolean;
}

/**
 * Keep the session RHS open while Voice occupies the Work slot. Mount outside
 * the panel's `isOpen` gate — otherwise Voice cannot open a closed sidebar.
 */
export function SessionWorkVoiceSidebarSyncView({
  setOpen,
  voiceInWorkSlot,
}: SessionWorkVoiceSidebarSyncProps) {
  const setSessionRhsHostCount = useSetAtom(voiceSessionRhsHostCountAtom);
  // Announce presence so placement falls back to overlay when this Sync is
  // gated off (Ask Heidi / Work / no session) — otherwise Voice vanishes.
  useLayoutEffect(() => {
    setSessionRhsHostCount((count) => count + 1);
    return () => setSessionRhsHostCount((count) => Math.max(0, count - 1));
  }, [setSessionRhsHostCount]);

  useVoiceHostedSidebarOpen(voiceInWorkSlot, setOpen);
  return null;
}

/**
 * The Evidence-chat side of the header's chrome, resolved by the app over Chat's
 * sidebar state and queries: which chat the header drives while Evidence only
 * holds the panel, the session's Evidence chats for the selector, and the three
 * hand-offs. Its callbacks keep the identities the app gives them.
 */
export interface SessionWorkPanelEvidenceChrome {
  /** The panel shows the classic Evidence session chat. */
  isEvidence: boolean;
  evidenceChatTitle: string | undefined;
  evidenceChats: SessionWorkProjectSelectorProps['evidenceChats'];
  openEvidenceChat: (chatId: string | null) => void;
  startNewEvidenceChat: () => void;
  /** A Work project pick: Work's own while Work owns the panel, else the hand-back. */
  handleSelectProject: (projectId: string) => void;
}

export interface SessionWorkPanelHeaderProps {
  sessionId: string;
  /** The session's linked patient profile: the host's, else the session record's. */
  associatedPatientProfileId?: string | null;
  /** The active project's id, or null while creating / in the empty state. */
  projectId?: string | null;
  /** The active project, or null/undefined while creating / cold-loading. */
  project?: ProjectDetail | null | undefined;
  /** True while a create round-trip is in flight — suppresses the project
   *  chrome (title edit / overflow menu) so an outgoing project can't flash
   *  controls over the optimistic transcript. Fullscreen and close stay;
   *  New chat hides until a project exists. */
  isCreatingProject?: boolean;
  readOnly?: boolean;
  /**
   * Set when the panel shows the classic Evidence session chat (Evidence only):
   * the chat's id, or null for a new one. The same chrome then drives that
   * chat, and picking a Work chat hands the panel back to Work.
   */
  evidenceChatId?: string | null;
  /** Renders a close (X) button when provided — omit for hosts whose own tab
   *  toggle already owns closing. */
  onClose?: () => void;
}

/**
 * What the app supplies: the Evidence chrome, the route for Open full screen, the
 * document library's preview clear for the composer reset, and the three header
 * pieces bound to the app's chat-list mutations, flags and routine reads.
 */
export interface SessionWorkPanelHeaderHost {
  evidenceChrome: SessionWorkPanelEvidenceChrome;
  onNavigate: NavigatePort;
  /** Clears the document library's preview. Module-level, so its identity is stable. */
  clearLibraryDocumentPreview: () => void;
  TitleCluster: ComponentType<ProjectTitleClusterProps>;
  HeaderActions: ComponentType<ProjectHeaderActionsProps>;
  ProjectSelector: ComponentType<SessionWorkProjectSelectorProps>;
}

/**
 * Header chrome for the session Work panel.
 *
 * Two layers:
 * - **Shell chrome**: New chat, overflow menu, and close. New chat shows only
 *   when a project (or an Evidence chat) exists; it hides while empty or
 *   creating. Open full screen lives in the menu.
 * - **Project chrome** (when not creating): session project selector +
 *   title cluster rename (when a project is active) + the same
 *   overflow menu (rename / save / copy, plus open full screen).
 *
 * Right cluster, left → right: [new chat?] [artifacts?] [overflow] [close?].
 * Project chrome mounts on the left so the shell buttons keep their positions
 * when a project resolves; DOM order = tab order.
 *
 * While creating, the title falls back to "New chat" so a stale project
 * can't flash a switcher over the optimistic transcript. Once create settles
 * (or the panel opens empty), the selector owns the title label.
 */
export const SessionWorkPanelHeaderView = ({
  sessionId,
  associatedPatientProfileId,
  projectId = null,
  project,
  isCreatingProject = false,
  readOnly = false,
  onClose,
  evidenceChatId,
  evidenceChrome,
  onNavigate,
  clearLibraryDocumentPreview,
  TitleCluster,
  HeaderActions,
  ProjectSelector,
}: SessionWorkPanelHeaderProps & SessionWorkPanelHeaderHost) => {
  const intl = useIntl();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const setActiveProject = useSetAtom(activeWorkPanelProjectIdAtom);
  const setSessionProject = useSetAtom(workPanelProjectIdAtomFamily(sessionId));
  const resetComposerContext = useResetProjectsComposerContextCore(
    clearLibraryDocumentPreview
  );
  const {
    isEvidence,
    evidenceChatTitle,
    evidenceChats,
    openEvidenceChat,
    startNewEvidenceChat,
    handleSelectProject,
  } = evidenceChrome;

  const startNewProject = useCallback(() => {
    // Clear both project slots so ``useWorkPanelProjectId`` resolves null and
    // the panel drops back to the empty composer; the next send starts a fresh
    // project. Reset the composer context too so it opens scoped to just this
    // session (no leftover linked chip / open artifact from the old chat).
    setActiveProject(null);
    setSessionProject(null);
    resetComposerContext();
  }, [setActiveProject, setSessionProject, resetComposerContext]);

  const openFullscreen = useCallback(() => {
    // Bound project → its Work page; empty panel → Work home (not Evidence).
    const href = isEvidence
      ? evidenceChatId
        ? getChatHref({ id: evidenceChatId, version: 1 })
        : LEGACY_CHAT_HOME_PATH
      : projectId
        ? getChatHref({ id: projectId, version: 2 })
        : WORK_CHAT_HOME_PATH;
    void onNavigate(href);
  }, [evidenceChatId, isEvidence, projectId, onNavigate]);

  const newProjectLabel = intl.formatMessage({
    defaultMessage: 'New chat',
    id: 'tseIyfNhYg',
    description: 'Session Work panel: start a new Work project in place',
  });
  const closeLabel = intl.formatMessage({
    defaultMessage: 'Close',
    id: 'CJ14jGH02a',
    description: 'Session Work panel: close button aria-label',
  });

  // A real project backs the panel — never the empty composer or an in-flight
  // create (an outgoing project must not flash controls over the optimistic
  // transcript). Bundled so both values narrow to non-null together.
  const active =
    !isCreatingProject && projectId && project ? { project, projectId } : null;
  // Already on an empty / creating surface — New would be a no-op (or race
  // the in-flight create). Hide it instead of leaving a disabled control.
  const showNewProject = isEvidence ? Boolean(evidenceChatId) : Boolean(active);

  const workFallback = (
    <TypographyP2 className="truncate font-medium text-text-primary">
      <FormattedMessage
        defaultMessage="New chat"
        id="FgMjFYdi2r"
        description="Session Work panel: header title while the project loads"
      />
    </TypographyP2>
  );

  const projectSelector = (
    <ProjectSelector
      sessionId={sessionId}
      associatedPatientProfileId={associatedPatientProfileId}
      currentProjectId={isEvidence ? null : projectId}
      currentProjectTitle={isEvidence ? evidenceChatTitle : project?.title}
      onSelectProject={handleSelectProject}
      evidenceChats={evidenceChats}
      currentEvidenceChatId={isEvidence ? evidenceChatId : null}
      onSelectEvidenceChat={openEvidenceChat}
    />
  );

  return (
    <header className="flex shrink-0 items-center justify-between gap-2 px-3 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {isCreatingProject ? (
          <TitleCluster
            project={null}
            projectId={null}
            fallback={workFallback}
            isEditing={false}
            onEditingChange={setIsEditingTitle}
            className="min-w-0 flex-1"
            inputClassName="w-full"
          />
        ) : (
          <TitleCluster
            project={active?.project}
            projectId={active?.projectId}
            leading={projectSelector}
            titleDisplay="none"
            isEditing={isEditingTitle}
            onEditingChange={setIsEditingTitle}
            className="min-w-0 flex-1"
            inputClassName="w-full"
          />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {showNewProject ? (
          <HeaderIconButton
            label={newProjectLabel}
            icon={SquarePen}
            onClick={isEvidence ? startNewEvidenceChat : startNewProject}
          />
        ) : null}
        {active ? <CreatedChatArtifactsButton projectId={active.projectId} /> : null}
        {active && !readOnly ? (
          <HeaderActions
            project={active.project}
            projectId={active.projectId}
            onRename={() => setIsEditingTitle(true)}
            onOpenFullscreen={openFullscreen}
          />
        ) : (
          <ProjectChatMenu onOpenFullscreen={openFullscreen} />
        )}
        {onClose ? (
          <HeaderIconButton label={closeLabel} icon={X} onClick={onClose} />
        ) : null}
      </div>
    </header>
  );
};

/** Keep the session RHS open while Voice occupies the Work slot. */
export function SessionWorkVoiceSidebarSync({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}) {
  const voiceInWorkSlot = getWorkHostPort('routines').useVoiceAt('session-rhs');
  return (
    <SessionWorkVoiceSidebarSyncView
      setOpen={setOpen}
      voiceInWorkSlot={voiceInWorkSlot}
    />
  );
}

/** The session Work header, bound to the Evidence chrome, the session and the route. */
export const SessionWorkPanelHeader = ({
  onSelectProject,
  ...props
}: SessionWorkPanelHeaderProps & {
  onSelectProject?: (projectId: string) => void;
}) => {
  const { sessionId, associatedPatientProfileId, evidenceChatId } = props;
  const evidenceChrome = useSessionWorkPanelEvidenceChrome({
    sessionId,
    evidenceChatId,
    onSelectProject,
  });
  const { data: sessionPatientProfileId } = useQuerySessionById({
    sessionId,
    select: (session) => session?.patient_profile?.id ?? null,
  });

  return (
    <SessionWorkPanelHeaderView
      {...props}
      associatedPatientProfileId={associatedPatientProfileId ?? sessionPatientProfileId}
      evidenceChrome={evidenceChrome}
      onNavigate={getWorkHostPort('shell').useNavigate()}
      clearLibraryDocumentPreview={clearLibraryDocumentPreview}
      TitleCluster={ProjectTitleCluster}
      HeaderActions={ProjectHeaderActions}
      ProjectSelector={SessionWorkProjectSelector}
    />
  );
};
